import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { 
  FileText, 
  DollarSign, 
  Users, 
  CheckCircle, 
  Clock, 
  Eye,
  Download,
  RefreshCw,
  Loader2,
  Building2,
  TrendingUp,
  ExternalLink,
  Plus,
  Trash2,
  Copy,
  FlaskConical,
  AlertTriangle
} from "lucide-react";

export const SponsorshipPanel = () => {
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [creatingTest, setCreatingTest] = useState(false);
  const [testCredentials, setTestCredentials] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingApp, setDeletingApp] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, statsRes] = await Promise.all([
        authFetch(`${API}/sponsorship/admin/applications`),
        authFetch(`${API}/sponsorship/admin/stats`)
      ]);

      if (appsRes.ok) {
        const data = await appsRes.json();
        setApplications(data.applications || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching sponsorship data:", error);
      toast.error("Failed to load sponsorship data");
    } finally {
      setLoading(false);
    }
  };

  const createTestApplication = async () => {
    setCreatingTest(true);
    try {
      const res = await authFetch(`${API}/sponsorship/admin/create-test-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (res.ok) {
        const data = await res.json();
        setTestCredentials(data.test_credentials);
        setTestDialogOpen(true);
        toast.success("Test application created!");
        fetchData(); // Refresh the list
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create test application");
      }
    } catch (error) {
      console.error("Error creating test application:", error);
      toast.error("Failed to create test application");
    } finally {
      setCreatingTest(false);
    }
  };

  const deleteApplication = async (applicationId) => {
    setDeleting(true);
    try {
      const res = await authFetch(`${API}/sponsorship/admin/delete-test-application/${applicationId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success("Application deleted");
        setDeleteDialogOpen(false);
        setDeletingApp(null);
        fetchData(); // Refresh the list
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to delete application");
      }
    } catch (error) {
      console.error("Error deleting application:", error);
      toast.error("Failed to delete application");
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      // Try the modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
        return;
      }
    } catch (err) {
      console.warn("Clipboard API failed, using fallback:", err);
    }
    
    // Fallback: Create a temporary textarea
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success("Copied to clipboard!");
      } else {
        toast.error("Failed to copy. Please copy manually.");
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error("Failed to copy. Please copy manually.");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'intake_pending': { label: 'Intake Pending', variant: 'outline', className: 'border-amber-500 text-amber-600' },
      'intake_complete': { label: 'Ready to Generate', variant: 'outline', className: 'border-blue-500 text-blue-600' },
      'packet_generated': { label: 'Packet Generated', variant: 'outline', className: 'border-emerald-500 text-emerald-600' },
      'enrolled': { label: 'Enrolled', variant: 'default', className: 'bg-purple-600' }
    };
    const config = statusConfig[status] || { label: status, variant: 'outline', className: '' };
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const viewDetails = (app) => {
    setSelectedApp(app);
    setDetailsOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-neutral-800 border-neutral-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.total || 0}</p>
                <p className="text-xs text-neutral-400">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-neutral-800 border-neutral-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.intake_pending || 0}</p>
                <p className="text-xs text-neutral-400">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-neutral-800 border-neutral-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.packet_generated || 0}</p>
                <p className="text-xs text-neutral-400">Generated</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-neutral-800 border-neutral-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.enrolled || 0}</p>
                <p className="text-xs text-neutral-400">Enrolled</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-neutral-800 border-neutral-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">${stats?.total_revenue?.toLocaleString() || 0}</p>
                <p className="text-xs text-neutral-400">Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Applications List */}
      <Card className="bg-neutral-800 border-neutral-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" />
            Sponsorship Applications
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={createTestApplication}
              disabled={creatingTest}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
            >
              {creatingTest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FlaskConical className="w-4 h-4 mr-2" />
              )}
              Create Test
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchData}
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {applications.length === 0 ? (
            <div className="text-center py-8 text-neutral-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No sponsorship applications yet</p>
              <p className="text-sm mt-1">Applications will appear here when clients purchase the Funding Gateway</p>
            </div>
          ) : (
            <div className="space-y-3">
              {applications.map((app) => (
                <div 
                  key={app.application_id}
                  className="flex items-center justify-between p-4 bg-neutral-900 rounded-lg border border-neutral-700 hover:border-neutral-600 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-500/10 rounded-full flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">
                        {app.intake_data?.business_name || app.user_name || "New Application"}
                      </p>
                      <p className="text-sm text-neutral-400">
                        {app.user_email} • {formatDate(app.created_at)}
                        {app.payment_status === "test_mode" && (
                          <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-400 text-xs">
                            TEST
                          </Badge>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {app.intake_data?.engagement_tier?.name && (
                      <span className="text-sm text-neutral-400 hidden md:inline">
                        {app.intake_data.engagement_tier.name}
                      </span>
                    )}
                    {getStatusBadge(app.status)}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => viewDetails(app)}
                      className="text-neutral-400 hover:text-white"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {app.payment_status === "test_mode" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDeletingApp(app);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-neutral-800 border-neutral-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-400" />
              Sponsorship Application Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Status</span>
                {getStatusBadge(selectedApp.status)}
              </div>

              {/* Application Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-neutral-400">Application ID</p>
                  <p className="font-mono text-sm">{selectedApp.application_id}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-400">Created</p>
                  <p>{formatDate(selectedApp.created_at)}</p>
                </div>
              </div>

              {/* Client Info */}
              <div className="border-t border-neutral-700 pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-400" />
                  Client Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-neutral-400">Name</p>
                    <p>{selectedApp.user_name || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-neutral-400">Email</p>
                    <p>{selectedApp.user_email || "N/A"}</p>
                  </div>
                </div>
              </div>

              {/* Intake Data */}
              {selectedApp.intake_data && Object.keys(selectedApp.intake_data).length > 0 && (
                <div className="border-t border-neutral-700 pt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-emerald-400" />
                    Business Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-neutral-400">Business Name</p>
                      <p>{selectedApp.intake_data.business_name || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400">Owner Name</p>
                      <p>{selectedApp.intake_data.owner_name || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400">Industry</p>
                      <p>{selectedApp.intake_data.industry || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400">Years in Operation</p>
                      <p>{selectedApp.intake_data.years_in_operation || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400">Revenue Range</p>
                      <p>{selectedApp.intake_data.revenue_range?.label || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400">Growth Goal</p>
                      <p>{selectedApp.intake_data.growth_goal_percent ? `${selectedApp.intake_data.growth_goal_percent}%` : "N/A"}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Engagement Selection */}
              {selectedApp.intake_data?.engagement_tier && (
                <div className="border-t border-neutral-700 pt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    Selected Engagement
                  </h4>
                  <div className="bg-neutral-900 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{selectedApp.intake_data.engagement_tier.name}</span>
                      <span className="text-emerald-400 font-bold">
                        ${selectedApp.intake_data.engagement_tier.price?.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-400 mt-1">
                      {selectedApp.intake_data.engagement_tier.duration}
                    </p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="border-t border-neutral-700 pt-4 flex gap-3">
                {selectedApp.status === "packet_generated" && (
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => window.open(`${API}/sponsorship/generate-packet/${selectedApp.user_id}`, '_blank')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Packet
                  </Button>
                )}
                <Button 
                  variant="outline"
                  className="border-neutral-600"
                  onClick={() => window.open(`/sponsorship/track?id=${selectedApp.application_id}`, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Public Page
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test Credentials Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="bg-neutral-800 border-neutral-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-amber-400" />
              Test Application Created
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              Use these credentials to test the Sponsorship Gateway flow
            </DialogDescription>
          </DialogHeader>
          
          {testCredentials && (
            <div className="space-y-4 pt-2">
              <div className="bg-neutral-900 rounded-lg p-4 space-y-3">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Email</p>
                  <div className="flex items-center justify-between">
                    <code className="text-emerald-400 text-sm">{testCredentials.email}</code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(testCredentials.email)}
                      className="text-neutral-400 hover:text-white h-7 px-2"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Password</p>
                  <div className="flex items-center justify-between">
                    <code className="text-emerald-400 text-sm">{testCredentials.password}</code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(testCredentials.password)}
                      className="text-neutral-400 hover:text-white h-7 px-2"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Application ID</p>
                  <code className="text-neutral-400 text-xs">{testCredentials.application_id}</code>
                </div>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-sm text-amber-400">
                  <strong>Next Steps:</strong>
                </p>
                <ol className="text-xs text-neutral-400 mt-2 space-y-1 list-decimal list-inside">
                  <li>Open a new browser/incognito window</li>
                  <li>Go to /login and use the credentials above</li>
                  <li>Complete the intake form</li>
                  <li>Generate the PDF packet</li>
                </ol>
              </div>

              <div className="flex gap-2">
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    const url = `${window.location.origin}/login`;
                    window.open(url, '_blank');
                  }}
                >
                  Open Login Page
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="border-neutral-600"
                  onClick={() => setTestDialogOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-neutral-800 border-neutral-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Delete Test Application
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              This will permanently delete the test application and associated user account.
            </DialogDescription>
          </DialogHeader>
          
          {deletingApp && (
            <div className="pt-2">
              <div className="bg-neutral-900 rounded-lg p-3 mb-4">
                <p className="text-sm text-white">{deletingApp.user_email}</p>
                <p className="text-xs text-neutral-500">{deletingApp.application_id}</p>
              </div>

              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  className="border-neutral-600"
                  onClick={() => {
                    setDeleteDialogOpen(false);
                    setDeletingApp(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => deleteApplication(deletingApp.application_id)}
                  disabled={deleting}
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Delete
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SponsorshipPanel;
